import * as RA from '@radix-ui/react-alert-dialog'
import { useId, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { cn } from './cn'
import { Input } from './Input'
import { usePrivateAttrs } from './privateArea'
import { useReturnFocusToOpener } from './returnFocus'
import { t } from '@/lib/i18n'

export interface ConfirmDialogProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode
  title: ReactNode
  /**
   * Text under the title, read out as the dialog's description. Plain text renders in a paragraph; anything
   * else (markup with fields or lists) renders in a div, so block content is valid HTML (Q-COMM-35).
   */
  description?: ReactNode
  /** Extra content between the description and the buttons, for example a field the action needs. */
  children?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Red confirm button (deletes, bans, leaving). */
  danger?: boolean
  /** When set, the user must type this exact text (for example the community name) to enable confirm. */
  confirmText?: string
  /** May return a promise; the dialog shows a spinner and closes when it resolves. */
  onConfirm: () => void | Promise<unknown>
  loading?: boolean
}

/** Confirmation prompt with optional type to confirm for destructive actions. */
export function ConfirmDialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  confirmLabel = t('Confirm'),
  cancelLabel = t('Cancel'),
  danger,
  confirmText,
  onConfirm,
  loading,
}: ConfirmDialogProps) {
  const [inner, setInner] = useState(defaultOpen ?? false)
  const open = openProp ?? inner
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const inputId = `confirm-${useId().replace(/:/g, '')}`
  const inputRef = useRef<HTMLInputElement>(null)
  // E2E-J8-06: Esc or Cancel gives focus back to what opened the dialog (Radix only does it for a trigger).
  const focus = useReturnFocusToOpener((e) => {
    if (confirmText === undefined) return
    e.preventDefault()
    inputRef.current?.focus()
  })
  const setOpen = (next: boolean) => {
    if (!next) setTyped('')
    if (openProp === undefined) setInner(next)
    onOpenChange?.(next)
  }
  const blocked = confirmText !== undefined && typed.trim() !== confirmText.trim()
  const working = busy || Boolean(loading)
  const priv = usePrivateAttrs()

  const handleConfirm = async () => {
    if (blocked || working) return
    try {
      const result = onConfirm()
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        setBusy(true)
        await result
      }
      setOpen(false)
    } catch {
      // Keep the dialog open; the caller shows the error (usually a toast).
    } finally {
      setBusy(false)
    }
  }

  return (
    <RA.Root open={open} onOpenChange={setOpen}>
      {trigger && <RA.Trigger asChild>{trigger}</RA.Trigger>}
      <RA.Portal>
        <RA.Overlay className="fixed inset-0 z-50 flex items-end justify-center bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out sm:items-center sm:p-6">
          <RA.Content
            {...priv}
            onOpenAutoFocus={focus.onOpenAutoFocus}
            onCloseAutoFocus={focus.onCloseAutoFocus}
            className={cn(
              'relative w-full bg-surface p-5 text-text shadow-pop outline-none sm:max-w-[420px] sm:rounded-modal sm:p-6',
              'rounded-t-modal pb-[max(20px,env(safe-area-inset-bottom))]',
              // Bounded like Dialog's panel (the overlay's sm:p-6 leaves 48px): a tall prompt (the leave question)
              // scrolls inside, so its buttons are never pushed off a small phone.
              'max-h-[calc(100dvh-24px)] overflow-y-auto overscroll-contain sm:max-h-[calc(100dvh-48px)]',
              'max-sm:data-[state=open]:animate-sheet-up sm:data-[state=open]:animate-dialog-in',
            )}
          >
            <RA.Title className="text-[18px] leading-7 font-bold tracking-[-0.01em]">{title}</RA.Title>
            {description && (typeof description === 'string' || typeof description === 'number') ? (
              <RA.Description className="mt-2 text-body text-muted">{description}</RA.Description>
            ) : description ? (
              <RA.Description asChild>
                <div className="mt-2 text-body text-muted">{description}</div>
              </RA.Description>
            ) : (
              <RA.Description className="sr-only">{t('Please confirm this action.')}</RA.Description>
            )}
            {children ? <div className="mt-4">{children}</div> : null}
            {confirmText !== undefined && (
              <div className="mt-4">
                <label htmlFor={inputId} className="mb-1.5 block text-meta font-semibold text-text">
                  <TypeToConfirm text={confirmText} />
                </label>
                <Input ref={inputRef} id={inputId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
              </div>
            )}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <RA.Cancel asChild>
                <Button variant="secondary" disabled={working}>
                  {cancelLabel}
                </Button>
              </RA.Cancel>
              <Button variant={danger ? 'danger' : 'primary'} disabled={blocked} loading={working} onClick={handleConfirm}>
                {confirmLabel}
              </Button>
            </div>
          </RA.Content>
        </RA.Overlay>
      </RA.Portal>
    </RA.Root>
  )
}

/** "Type {text} to confirm" with the text in bold, translated as one sentence. */
function TypeToConfirm({ text }: { text: string }) {
  const template = t('Type {text} to confirm')
  const at = template.indexOf('{text}')
  if (at < 0) return <>{template} <span className="font-bold">{text}</span></>
  return (
    <>
      {template.slice(0, at)}
      <span className="font-bold">{text}</span>
      {template.slice(at + '{text}'.length)}
    </>
  )
}
